import type { AdCreative, AdEventType, AdSlot, Category, Channel, FeaturedChannel, FeaturedSettings, HomeAd, HomeAdDecisions, HomeAds, HomeAdSlot, PartnerTheme, PlaybackAction, PlaybackContentType, ScheduleChannel, Session, TvPairing, TvPairingStatus, VodProgress, VodTitle } from '../types';
import { parseVast } from './vast';

interface Envelope<T> {
  success: boolean;
  data: T;
  error?: string;
  message?: string;
}

const API_ROOT = '/api/v1';
const AD_REQUEST_TIMEOUT_MS = 5_000;

export class ApiRequestError extends Error {
  constructor(message: string, readonly status: number) { super(message); this.name = 'ApiRequestError'; }
}

export interface AdRequestOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

function adRequestLifetime(options: AdRequestOptions, maximumMs: number) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (options.signal?.aborted) abort();
  else options.signal?.addEventListener('abort', abort, { once: true });
  const timeout = window.setTimeout(abort, Math.max(0, Math.min(options.timeoutMs ?? maximumMs, maximumMs)));
  return {
    signal: controller.signal,
    check() {
      if (controller.signal.aborted) {
        const error = new Error('Ad request cancelled.');
        error.name = 'AbortError';
        throw error;
      }
    },
    dispose() {
      window.clearTimeout(timeout);
      options.signal?.removeEventListener('abort', abort);
    },
  };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_ROOT}${path}`, init);
  let payload: Envelope<T>;
  try {
    payload = await response.json() as Envelope<T>;
  } catch {
    throw new ApiRequestError(`A API respondeu em formato inválido (${response.status}).`, response.status);
  }
  if (!response.ok || payload.success === false) {
    throw new ApiRequestError(payload.error || payload.message || `Falha na API (${response.status}).`, response.status);
  }
  return payload.data;
}

export async function signIn(email: string, password: string): Promise<Session> {
  const form = new URLSearchParams({ email, password, platform: 'SmartTv' });
  const data = await request<any>('/account/signin', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
    body: form.toString(),
  });
  const token = data.token || data.user?.token;
  if (!token) throw new Error('A API não devolveu um token de sessão.');
  return {
    email: data.email || data.user?.email || email,
    name: data.name || data.first_name || data.user?.name || 'Espectador',
    token,
    partner: data.partner || data.user?.partner || null,
    contentToken: data.content_token?.token || data.user?.content_token?.token,
  };
}

export function loadPartnerTheme(slug?: string | null): Promise<PartnerTheme> {
  const query = slug ? `?${new URLSearchParams({ slug })}` : '';
  return request<PartnerTheme>(`/account/partner/theme${query}`);
}

export function createTvPairing(partner?: string): Promise<TvPairing> {
  return request<TvPairing>('/account/tv/pairing', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ partner: partner || undefined }),
  });
}

export async function loadTvPairing(code: string): Promise<TvPairingStatus> {
  const data = await request<any>(`/account/tv/pairing/${code}`);
  if (data.status !== 'approved') return data as TvPairingStatus;
  const raw = data.session;
  return {
    status: 'approved',
    session: {
      email: raw.email,
      name: raw.name || raw.first_name || 'Espectador',
      token: raw.token,
      partner: raw.partner || null,
      contentToken: raw.content_token?.token,
    },
  };
}

export function approveTvPairing(code: string, email: string, password: string) {
  return request<{ status: 'approved'; partner: string }>(`/account/tv/pairing/${code}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
}

export function formatApiDate(date = new Date()): string {
  const parts = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(date);
  const value = (type: string) => parts.find(part => part.type === type)?.value || '';
  return `${value('day')}/${value('month')}/${value('year')}`;
}

export async function loadCatalog(): Promise<{
  channels: Channel[];
  categories: Category[];
  schedules: ScheduleChannel[];
  movies: VodTitle[];
  series: VodTitle[];
}> {
  const query = new URLSearchParams({
    date: formatApiDate(),
    language: 'pt',
    country: 'BR',
    time_zone: 'America/Sao_Paulo',
  });
  const [channels, categories, schedules, movies, series] = await Promise.all([
    request<Channel[]>('/brand?language=pt&country=BR'),
    request<Category[]>('/categories?language=pt&country=BR'),
    request<ScheduleChannel[]>(`/schedules/all?${query}`),
    request<VodTitle[]>('/vod?kind=movie'),
    request<VodTitle[]>('/vod?kind=series'),
  ]);
  return { channels, categories, schedules, movies, series };
}

export async function loadFeatured(): Promise<{ items: FeaturedChannel[]; settings: FeaturedSettings }> {
  const [items, settings] = await Promise.all([
    request<FeaturedChannel[]>('/features?language=pt&country=BR'),
    request<FeaturedSettings>('/features/settings'),
  ]);
  return {
    items: items.filter(item => item.type_mode_name === 'Channel' && Boolean(item.channel))
      .sort((a, b) => a.position - b.position),
    settings,
  };
}

export function loadSchedules(date: Date): Promise<ScheduleChannel[]> {
  const query = new URLSearchParams({
    date: formatApiDate(date), language: 'pt', country: 'BR', time_zone: 'America/Sao_Paulo',
  });
  return request<ScheduleChannel[]>(`/schedules/all?${query}`);
}

export function loadVodTitle(id: number): Promise<VodTitle> {
  return request<VodTitle>(`/vod/${id}`);
}

function tokenHeaders(token: string, contentToken?: string) {
  return {
    Authorization: `Token ${token}`, 'Content-Type': 'application/json',
    ...(contentToken ? { Contentsession: contentToken } : {}),
  };
}

function tvPlatform() {
  const runtime = window as unknown as { tizen?: unknown; webOS?: unknown };
  if (runtime.tizen) return 'tizen';
  if (runtime.webOS) return 'webos';
  return 'smart-tv';
}

export function startPlayback(
  token: string, sessionId: string, contentType: PlaybackContentType, contentId: number,
) {
  return request<{ session_id: string; created: boolean }>('/playback/sessions', {
    method: 'POST', headers: {
      ...tokenHeaders(token), Platform: tvPlatform(), Device: navigator.userAgent.slice(0, 80),
    },
    body: JSON.stringify({
      session_id: sessionId, content_type: contentType, content_id: contentId,
      client_family: 'tv-next', client_version: '1',
    }),
  });
}

export function updatePlayback(
  token: string, sessionId: string, sequence: number, action: PlaybackAction,
  positionSeconds: number, reason = '',
) {
  return request<{ session_id: string; qualified: boolean }>(`/playback/sessions/${sessionId}`, {
    method: 'PATCH', headers: {
      ...tokenHeaders(token), Platform: tvPlatform(), Device: navigator.userAgent.slice(0, 80),
    },
    body: JSON.stringify({
      sequence, action, reason, position_seconds: Math.max(0, Math.floor(positionSeconds || 0)),
    }),
  });
}

const AD_VIEWER_KEY = 'innova-ad-viewer-id';

function adViewerId(): string {
  const stored = localStorage.getItem(AD_VIEWER_KEY);
  if (stored) return stored;
  const value = typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
      const random = Math.floor(Math.random() * 16);
      const digit = character === 'x' ? random : (random & 0x3) | 0x8;
      return digit.toString(16);
    });
  localStorage.setItem(AD_VIEWER_KEY, value);
  return value;
}

function adHeaders(token: string | null, contentToken?: string): Record<string, string> {
  let effectiveContentToken = contentToken;
  if (!effectiveContentToken && token) {
    try {
      effectiveContentToken = (JSON.parse(localStorage.getItem('innova-tv-session') || '{}') as Session).contentToken;
    } catch { /* A corrupt legacy session is handled by App. */ }
  }
  return token ? tokenHeaders(token, effectiveContentToken) : {
    'Content-Type': 'application/json', 'X-Ad-Viewer-ID': adViewerId(),
  };
}

export function loadContinueWatching(token: string): Promise<VodProgress[]> {
  return request<VodProgress[]>('/vod/progress', { headers: tokenHeaders(token) });
}

export function loadVodFavorites(token: string): Promise<VodTitle[]> {
  return request<VodTitle[]>('/vod/favorites', { headers: tokenHeaders(token) });
}

export function setVodFavorite(token: string, titleId: number, favorite: boolean) {
  return request<{ title_id: number; favorite: boolean }>(`/vod/favorites/${titleId}`, {
    method: favorite ? 'PUT' : 'DELETE',
    headers: tokenHeaders(token),
  });
}

export async function loadAd(
  token: string | null, slot: AdSlot, partner?: string | null, content?: string,
  markerId?: number, screen?: string, options: AdRequestOptions = {}, contentToken?: string,
): Promise<AdCreative | null> {
  // A Home consulta todo o inventário em paralelo. Em TVs e redes mais lentas,
  // 1,5 s interrompia respostas 200 antes da leitura do JSON e removia peças válidas.
  const lifetime = adRequestLifetime(options, AD_REQUEST_TIMEOUT_MS);
  try {
    lifetime.check();
    const query = new URLSearchParams({ slot, platform: 'smart-tv' });
    if (partner) query.set('partner', partner);
    if (content) query.set('content', content);
    if (markerId) query.set('marker', String(markerId));
    if (screen) query.set('screen', screen);
    const decision = await request<AdCreative | null>(`/ads/decision?${query}`, {
      headers: adHeaders(token, contentToken), signal: lifetime.signal,
    });
    lifetime.check();
    return decision;
  } finally {
    lifetime.dispose();
  }
}

export async function resolveVastAd(
  token: string | null, ad: AdCreative, options: AdRequestOptions = {},
): Promise<AdCreative> {
  if (ad.media_kind !== 'vast' || !ad.vast_url) throw new Error('VAST decision is incomplete.');
  const lifetime = adRequestLifetime(options, 1_500);
  try {
    lifetime.check();
    const response = await fetch(ad.vast_url, {
      headers: adHeaders(token), signal: lifetime.signal,
    });
    if (!response.ok) throw new Error(`VAST request failed (${response.status}).`);
    const media = parseVast(await response.text());
    lifetime.check();
    return {
      ...ad,
      video: media.mediaUrl,
      duration_seconds: media.durationSeconds,
      skippable_after_seconds: media.skippableAfterSeconds,
    };
  } finally {
    lifetime.dispose();
  }
}

export function loadHomeAd(token: string): Promise<HomeAd | null> {
  return loadAd(token, 'tv_home_banner');
}

const HOME_AD_SLOTS: HomeAdSlot[] = [
  'tv_home_banner', 'home_hero', 'home_banner_large', 'home_card_landscape',
  'home_banner_wide', 'home_card_square',
  'rail_sponsored_card',
  'home_card_portrait', 'home_card_compact', 'home_recommendation_card',
  'home_section_banner_1', 'home_section_banner_2', 'home_section_banner_3',
  'home_sponsored_rail', 'home_collection_ad', 'home_screensaver',
  'epg_sponsor', 'partner_external_feature',
];

export async function loadHomeAds(
  token: string, options: AdRequestOptions = {}, contentToken?: string,
): Promise<HomeAdDecisions> {
  const lifetime = adRequestLifetime(options, AD_REQUEST_TIMEOUT_MS);
  try {
    lifetime.check();
    const query = new URLSearchParams({ platform: 'smart-tv' });
    const decisions = await request<Partial<Record<AdSlot, AdCreative | null>>>(
      `/ads/decisions/home?${query}`, { headers: adHeaders(token, contentToken), signal: lifetime.signal },
    );
    lifetime.check();
    const available = HOME_AD_SLOTS.flatMap(slot => {
      const creative = decisions[slot];
      return creative?.slot === slot ? [[slot, creative] as const] : [];
    });
    const partnerHero = decisions.partner_hero;
    return {
      ads: Object.fromEntries(available) as HomeAds,
      partnerHero: partnerHero?.slot === 'partner_hero' ? partnerHero : null,
    };
  } finally {
    lifetime.dispose();
  }
}

export function recordAdEvent(
  token: string | null, ad: AdCreative, eventType: AdEventType, eventKey: string,
  signal?: AbortSignal,
): Promise<{ id: number; created: boolean }> {
  return request<{ id: number; created: boolean }>('/ads/events', {
    method: 'POST',
    signal,
    headers: adHeaders(token),
    body: JSON.stringify({
      event_key: eventKey, event_type: eventType, slot: ad.slot, creative_id: ad.id,
      decision_id: ad.decision_id,
      viewer_id: token ? undefined : adViewerId(),
    }),
  });
}

export function saveWatchProgress(
  token: string,
  item: { title_id?: number; episode_id?: number },
  positionSeconds: number,
  durationSeconds: number,
): Promise<VodProgress> {
  return request<VodProgress>('/vod/progress', {
    method: 'PUT',
    headers: tokenHeaders(token),
    body: JSON.stringify({
      title_id: item.episode_id ? undefined : item.title_id,
      episode_id: item.episode_id,
      position_seconds: positionSeconds,
      duration_seconds: durationSeconds,
    }),
  });
}
